import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Separator } from "../../../components/ui/separator";
import {
  getSamConfig,
  updateSamConfig,
  formatTimestamp,
  COMMON_NAICS_CODES,
} from "../services/samConfigService";
import { Save, Plus, X, Check, AlertCircle, RefreshCw, Database, Zap, History, FileSearch } from "lucide-react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../../config/firebase";

export default function SamConfig() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [newNaicsCode, setNewNaicsCode] = useState("");

  // Historical awards ingest state
  const [ingestingAwards, setIngestingAwards] = useState(false);
  const [computingSimilarity, setComputingSimilarity] = useState(false);
  const [ingestResult, setIngestResult] = useState(null);
  const [similarityResult, setSimilarityResult] = useState(null);

  // FPDS enrichment state
  const [enrichingFpds, setEnrichingFpds] = useState(false);
  const [fpdsResult, setFpdsResult] = useState(null);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getSamConfig();
      setConfig(data);
    } catch (err) {
      console.error("Error fetching config:", err);
      setError("Failed to load configuration");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(false);
      await updateSamConfig(config);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error("Error saving config:", err);
      setError("Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  const addNaicsCode = () => {
    const code = newNaicsCode.trim();
    if (!code) return;
    if (config.naics_codes.includes(code)) {
      setError("NAICS code already exists");
      return;
    }
    if (!/^\d{6}$/.test(code)) {
      setError("NAICS code must be 6 digits");
      return;
    }
    setConfig({
      ...config,
      naics_codes: [...config.naics_codes, code],
    });
    setNewNaicsCode("");
    setError(null);
  };

  const removeNaicsCode = (code) => {
    setConfig({
      ...config,
      naics_codes: config.naics_codes.filter((c) => c !== code),
    });
  };

  const addCommonNaicsCode = (code) => {
    if (config.naics_codes.includes(code)) return;
    setConfig({
      ...config,
      naics_codes: [...config.naics_codes, code],
    });
  };

  // Ingest historical awards from USAspending
  const handleIngestAwards = async () => {
    try {
      setIngestingAwards(true);
      setIngestResult(null);
      setError(null);

      const ingestHistoricalAwards = httpsCallable(functions, "ingestHistoricalAwards");

      const result = await ingestHistoricalAwards({
        yearsBack: config.historical_window_years || 5,
        limit: 100, // USAspending API max is 100 per request
      });

      setIngestResult(result.data);
    } catch (err) {
      console.error("Error ingesting awards:", err);
      // Firebase callable functions return error details in err.code and err.details
      const errorMessage = err.details || err.message || "Unknown error";
      setError(`Failed to ingest historical awards: ${errorMessage}`);
    } finally {
      setIngestingAwards(false);
    }
  };

  // Compute similarity scores for all opportunities
  const handleComputeSimilarity = async () => {
    try {
      setComputingSimilarity(true);
      setSimilarityResult(null);
      setError(null);

      const batchComputeSimilarity = httpsCallable(functions, "batchComputeSimilarity");

      const result = await batchComputeSimilarity({ limit: 100 });

      setSimilarityResult(result.data);
    } catch (err) {
      console.error("Error computing similarity:", err);
      setError(`Failed to compute similarity: ${err.message}`);
    } finally {
      setComputingSimilarity(false);
    }
  };

  // Enrich historical awards with FPDS detail
  const handleEnrichFpds = async () => {
    try {
      setEnrichingFpds(true);
      setFpdsResult(null);
      setError(null);

      const enrichWithFpds = httpsCallable(functions, "enrichWithFpds");

      const result = await enrichWithFpds({ limit: 50 });

      setFpdsResult(result.data);
    } catch (err) {
      console.error("Error enriching with FPDS:", err);
      const errorMessage = err.details || err.message || "Unknown error";
      setError(`Failed to enrich with FPDS: ${errorMessage}`);
    } finally {
      setEnrichingFpds(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          Loading configuration...
        </CardContent>
      </Card>
    );
  }

  if (!config) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <p className="text-destructive mb-4">{error || "Failed to load configuration"}</p>
          <Button onClick={fetchConfig}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* NAICS Configuration */}
      <Card>
        <CardHeader>
          <CardTitle>SAM.gov Opportunity Search</CardTitle>
          <CardDescription>
            Configure which NAICS codes to search for on SAM.gov
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Current NAICS codes */}
          <div>
            <Label className="mb-2 block">Tracked NAICS Codes</Label>
            <div className="flex flex-wrap gap-2">
              {config.naics_codes.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No NAICS codes configured. Add codes below to start tracking opportunities.
                </p>
              ) : (
                config.naics_codes.map((code) => {
                  const info = COMMON_NAICS_CODES.find((c) => c.code === code);
                  return (
                    <Badge
                      key={code}
                      variant="secondary"
                      className="gap-1 pr-1"
                    >
                      <code>{code}</code>
                      {info && (
                        <span className="text-xs opacity-70 ml-1">
                          {info.description.slice(0, 20)}...
                        </span>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-4 w-4 ml-1 hover:bg-destructive hover:text-destructive-foreground"
                        onClick={() => removeNaicsCode(code)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </Badge>
                  );
                })
              )}
            </div>
          </div>

          <Separator />

          {/* Add custom NAICS */}
          <div>
            <Label className="mb-2 block">Add NAICS Code</Label>
            <div className="flex gap-2">
              <Input
                placeholder="Enter 6-digit NAICS code"
                value={newNaicsCode}
                onChange={(e) => setNewNaicsCode(e.target.value)}
                maxLength={6}
                className="w-48"
              />
              <Button onClick={addNaicsCode} disabled={!newNaicsCode.trim()}>
                <Plus className="h-4 w-4 mr-2" />
                Add
              </Button>
            </div>
          </div>

          <Separator />

          {/* Common NAICS suggestions */}
          <div>
            <Label className="mb-2 block">Common NAICS Codes</Label>
            <p className="text-sm text-muted-foreground mb-2">
              Click to add common government contracting NAICS codes
            </p>
            <div className="flex flex-wrap gap-2">
              {COMMON_NAICS_CODES.map((item) => (
                <Button
                  key={item.code}
                  variant="outline"
                  size="sm"
                  disabled={config.naics_codes.includes(item.code)}
                  onClick={() => addCommonNaicsCode(item.code)}
                  className="text-xs"
                >
                  {item.code}
                  {config.naics_codes.includes(item.code) && (
                    <Check className="h-3 w-3 ml-1" />
                  )}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Similarity Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Similarity Engine Settings</CardTitle>
          <CardDescription>
            Configure how SAM.gov opportunities are matched to historical USAspending awards
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="window">Historical Window (Years)</Label>
              <Input
                id="window"
                type="number"
                min={1}
                max={10}
                value={config.historical_window_years || 5}
                onChange={(e) =>
                  setConfig({
                    ...config,
                    historical_window_years: parseInt(e.target.value) || 5,
                  })
                }
                className="w-24 mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                How far back to look for historical awards on USAspending
              </p>
            </div>

            <div>
              <Label>Similarity Thresholds</Label>
              <div className="mt-1 space-y-2">
                <div className="flex items-center gap-2">
                  <Badge variant="success">High</Badge>
                  <span className="text-sm">
                    &ge; {((config.similarity_thresholds?.high || 0.7) * 100).toFixed(0)}%
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="warning">Medium</Badge>
                  <span className="text-sm">
                    &ge; {((config.similarity_thresholds?.medium || 0.4) * 100).toFixed(0)}%
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">Low</Badge>
                  <span className="text-sm">
                    &lt; {((config.similarity_thresholds?.medium || 0.4) * 100).toFixed(0)}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Historical Awards Ingest */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Historical Awards (USAspending)
          </CardTitle>
          <CardDescription>
            Fetch historical contract awards from USAspending.gov for similarity matching
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-4">
            <Button
              onClick={handleIngestAwards}
              disabled={ingestingAwards || config.naics_codes.length === 0}
            >
              <Database className="h-4 w-4 mr-2" />
              {ingestingAwards ? "Ingesting..." : "Ingest Historical Awards"}
            </Button>

            <Button
              variant="secondary"
              onClick={handleComputeSimilarity}
              disabled={computingSimilarity}
            >
              <Zap className="h-4 w-4 mr-2" />
              {computingSimilarity ? "Computing..." : "Compute Similarity"}
            </Button>
          </div>

          {config.naics_codes.length === 0 && (
            <p className="text-sm text-amber-600">
              <AlertCircle className="h-4 w-4 inline mr-1" />
              Configure NAICS codes above before ingesting historical awards
            </p>
          )}

          {ingestResult && (
            <div className="p-3 bg-muted rounded-md text-sm">
              <p className="font-medium text-green-600 mb-1">
                <Check className="h-4 w-4 inline mr-1" />
                Historical Awards Ingest Complete
              </p>
              <p>Awards fetched: {ingestResult.awards_fetched}</p>
              <p>Awards stored: {ingestResult.awards_stored}</p>
              <p>Duration: {(ingestResult.duration_ms / 1000).toFixed(1)}s</p>
            </div>
          )}

          {similarityResult && (
            <div className="p-3 bg-muted rounded-md text-sm">
              <p className="font-medium text-green-600 mb-1">
                <Check className="h-4 w-4 inline mr-1" />
                Similarity Computation Complete
              </p>
              <p>Opportunities processed: {similarityResult.opportunities_processed}</p>
              <p>With matches: {similarityResult.with_matches}</p>
              <p>Duration: {(similarityResult.duration_ms / 1000).toFixed(1)}s</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* FPDS Enrichment */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSearch className="h-5 w-5" />
            FPDS Contract Details
          </CardTitle>
          <CardDescription>
            Enrich historical awards with additional contract details from FPDS.gov
            (vendor CAGE codes, office hierarchy, contract modifications)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button
            onClick={handleEnrichFpds}
            disabled={enrichingFpds}
          >
            <FileSearch className="h-4 w-4 mr-2" />
            {enrichingFpds ? "Enriching..." : "Enrich with FPDS"}
          </Button>

          <p className="text-sm text-muted-foreground">
            This process fetches additional contract details from FPDS.gov for historical awards
            that haven't been enriched yet. FPDS has stricter rate limits, so this may take a while.
          </p>

          {fpdsResult && (
            <div className="p-3 bg-muted rounded-md text-sm">
              <p className="font-medium text-green-600 mb-1">
                <Check className="h-4 w-4 inline mr-1" />
                FPDS Enrichment Complete
              </p>
              <p>Awards processed: {fpdsResult.awards_processed}</p>
              <p>Successfully enriched: {fpdsResult.enriched}</p>
              <p>Failed: {fpdsResult.failed}</p>
              <p>Duration: {(fpdsResult.duration_ms / 1000).toFixed(1)}s</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Status messages and save button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {error && (
            <div className="flex items-center gap-2 text-destructive text-sm">
              <AlertCircle className="h-4 w-4" />
              {error}
            </div>
          )}
          {success && (
            <div className="flex items-center gap-2 text-green-600 text-sm">
              <Check className="h-4 w-4" />
              Configuration saved successfully
            </div>
          )}
        </div>

        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchConfig} disabled={loading}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Reset
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            {saving ? "Saving..." : "Save Configuration"}
          </Button>
        </div>
      </div>

      {config.last_updated && (
        <p className="text-xs text-muted-foreground text-right">
          Last updated: {formatTimestamp(config.last_updated)}
        </p>
      )}
    </div>
  );
}
